import { useEffect, useState } from "react";
import {
  browserOptOutSignal,
  isDefaultOffRegion,
  isRecordingOff,
  isReplayFeatureAvailable,
  readChoice,
  subscribeConsent,
} from "../../utils/replay-consent";
import { documentCouldRecord, subscribeDocument } from "../../utils/replay-document";

const OFF = Object.freeze({ available: false });

/*
 * What the Settings switch ("Help improve QLearn") shows right now. With the
 * build flag off, in the native app or behind the third-party kill switch it
 * reads nothing at all and returns OFF, and the section renders nothing.
 *
 *   - gpc / dnt: the browser sends Global Privacy Control / Do Not Track, so
 *     recording is off and the switch cannot turn it on (GPC is named when
 *     both are sent);
 *   - choice: "allowed", "declined" or null (nothing stored);
 *   - regionDefaultOff: nothing stored and a European or UK time zone, so the
 *     default is off;
 *   - on: recording may run in this browser (not off, no GPC or DNT).
 */
export default function useReplayConsent() {
  const [, setTick] = useState(0);
  const available = isReplayFeatureAvailable();

  useEffect(() => {
    if (!available) return undefined;
    const bump = () => setTick((n) => n + 1);
    const offConsent = subscribeConsent(bump);
    // Another tab's choice arrives as a storage event.
    window.addEventListener("storage", bump);
    return () => {
      offConsent();
      window.removeEventListener("storage", bump);
    };
  }, [available]);

  if (!available) return OFF;
  const signal = browserOptOutSignal();
  const choice = readChoice();
  return {
    available,
    gpc: signal === "gpc",
    dnt: signal === "dnt",
    choice,
    regionDefaultOff: choice === null && isDefaultOffRegion(),
    on: signal === null && !isRecordingOff(),
  };
}

// Whether recording may run in this document (the daily page drops the
// unlock button's link target, which holds the grant, whenever it may). Off
// the feature, after turning it off, with GPC or DNT or on a tainted page:
// false.
export function useRecordingMayRun() {
  const [mayRun, setMayRun] = useState(() => documentCouldRecord());
  useEffect(() => {
    if (!isReplayFeatureAvailable()) return undefined;
    const update = () => setMayRun(documentCouldRecord());
    update();
    const offDocument = subscribeDocument(update);
    const offConsent = subscribeConsent(update);
    return () => {
      offDocument();
      offConsent();
    };
  }, []);
  return mayRun;
}
